import type { PyqTrendData } from "./types";

/**
 * SINGLE SOURCE OF TRUTH for the PYQ Predictor.
 * Provided dataset — values are used exactly as supplied.
 */
export const completePyqTrendData: PyqTrendData = {
  jee: {
    physics: [
      // Class 11th
      {
        id: "jee-phy-11-01",
        chapter: "Units, Dimensions & Experimental Errors",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "95%",
        recentTrend: [1, 2, 2, 2, 2],
        isLocked: false,
        subTopics: [
          { name: "Vernier Calliper & Screw Gauge Zero Errors", probability: "94%", primaryType: "Numerical (85%)" },
          { name: "Dimensional Formula Deductions", probability: "88%", primaryType: "Matching (75%)" },
          { name: "Percentage Error Propagation in Formulas", probability: "85%", primaryType: "Numerical (80%)" }
        ],
        predictionNote: "Screw gauge least count with positive/negative zero error is a regular shift question."
      },
      {
        id: "jee-phy-11-02",
        chapter: "Motion in 1D & 2D (Kinematics)",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "90%",
        recentTrend: [2, 1, 2, 2, 1],
        isLocked: false,
        subTopics: [
          { name: "Projectile on Inclined Plane & Max Range", probability: "82%", primaryType: "Numerical (70%)" },
          { name: "Relative Motion (River-Boat / Rain-Man)", probability: "86%", primaryType: "Single Correct (65%)" },
          { name: "Variable Acceleration Calculus ($v\\,dv/dx$)", probability: "80%", primaryType: "Numerical (75%)" }
        ],
        predictionNote: "Kinematics combined with variable resistance force like $a = -kv$."
      },
      {
        id: "jee-phy-11-03",
        chapter: "Laws of Motion & Friction",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "88%",
        recentTrend: [2, 1, 2, 1, 2],
        isLocked: true,
        subTopics: [
          { name: "Block-on-Block Friction Slipping Conditions", probability: "85%", primaryType: "Numerical (80%)" },
          { name: "Constraint Motion in Pulley Systems", probability: "80%", primaryType: "Single Correct (70%)" },
          { name: "Pseudo Force in Accelerating Frames", probability: "76%", primaryType: "Single Correct (65%)" }
        ],
        predictionNote: "Two blocks stacked with friction between surfaces under external force."
      },
      {
        id: "jee-phy-11-04",
        chapter: "Work, Energy, Power & Circular Motion",
        classLevel: "11th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "92%",
        recentTrend: [2, 2, 2, 2, 3],
        isLocked: true,
        subTopics: [
          { name: "Work-Energy Theorem with Non-Conservative Forces", probability: "90%", primaryType: "Numerical (75%)" },
          { name: "Vertical Circular Motion Critical Velocities", probability: "84%", primaryType: "Single Correct (70%)" },
          { name: "Power as Function of Time & Velocity", probability: "79%", primaryType: "Numerical (70%)" }
        ],
        predictionNote: "Calculations involving variable spring potential energy with damping work."
      },
      {
        id: "jee-phy-11-05",
        chapter: "System of Particles, Center of Mass & Collisions",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "89%",
        recentTrend: [1, 2, 2, 1, 2],
        isLocked: true,
        subTopics: [
          { name: "Coefficient of Restitution (e) in 1D & Oblique Collisions", probability: "88%", primaryType: "Numerical (80%)" },
          { name: "Center of Mass of Truncated / Cut-out Bodies", probability: "82%", primaryType: "Numerical (75%)" }
        ],
        predictionNote: "Spring-block system momentum and energy conservation after impulse."
      },
      {
        id: "jee-phy-11-06",
        chapter: "Rotational Dynamics",
        classLevel: "11th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "Moderate Yield / High Effort",
        repeatProbability: "92%",
        recentTrend: [2, 2, 2, 3, 2],
        isLocked: true,
        subTopics: [
          { name: "Moment of Inertia Theorems (Parallel & Perpendicular)", probability: "89%", primaryType: "Single Correct (70%)" },
          { name: "Rolling without Slipping on Rough Inclined Plane", probability: "85%", primaryType: "Numerical (80%)" },
          { name: "Conservation of Angular Momentum About Instantaneous Axis", probability: "82%", primaryType: "Numerical (75%)" }
        ],
        predictionNote: "Calculation of minimum friction coefficient required for pure rolling."
      },
      {
        id: "jee-phy-11-07",
        chapter: "Gravitation",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "92%",
        recentTrend: [1, 2, 1, 2, 2],
        isLocked: true,
        subTopics: [
          { name: "Gravitational Potential & Escape Speed", probability: "90%", primaryType: "Numerical (80%)" },
          { name: "Kepler's 3rd Law & Orbital Energy of Satellites", probability: "86%", primaryType: "Single Correct (75%)" },
          { name: "Variation of g with Altitude, Depth and Latitude", probability: "84%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Energy needed to shift a satellite from radius $R_1$ to $R_2$."
      },
      {
        id: "jee-phy-11-08",
        chapter: "Mechanical Properties of Solids (Elasticity)",
        classLevel: "11th",
        avgQuestions: 1.0,
        avgMarks: 4,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "85%",
        recentTrend: [1, 1, 1, 1, 1],
        isLocked: true,
        subTopics: [
          { name: "Young's Modulus & Elongation Under Self-Weight", probability: "86%", primaryType: "Numerical (80%)" },
          { name: "Stress-Strain Curve & Elastic Potential Energy Density", probability: "80%", primaryType: "Single Correct (65%)" }
        ],
        predictionNote: "Series-parallel equivalent wire systems with composite thermal expansion."
      },
      {
        id: "jee-phy-11-09",
        chapter: "Fluid Mechanics & Hydrodynamics",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "Moderate Yield / Moderate Effort",
        repeatProbability: "86%",
        recentTrend: [1, 2, 1, 2, 1],
        isLocked: true,
        subTopics: [
          { name: "Bernoulli's Equation & Torricelli's Law of Efflux", probability: "88%", primaryType: "Numerical (75%)" },
          { name: "Terminal Velocity & Stokes Law with Viscosity", probability: "82%", primaryType: "Numerical (70%)" },
          { name: "Surface Tension & Excess Pressure in Bubbles/Drops", probability: "80%", primaryType: "Single Correct (65%)" }
        ],
        predictionNote: "Time taken to empty a tank through a small hole at the bottom."
      },
      {
        id: "jee-phy-11-10",
        chapter: "Thermal Properties of Matter & Calorimetry",
        classLevel: "11th",
        avgQuestions: 1.0,
        avgMarks: 4,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "84%",
        recentTrend: [1, 1, 1, 1, 1],
        isLocked: true,
        subTopics: [
          { name: "Stefan-Boltzmann Law & Newton's Law of Cooling", probability: "86%", primaryType: "Numerical (80%)" },
          { name: "Thermal Resistance & Equivalent Rod Conductivity", probability: "82%", primaryType: "Numerical (75%)" },
          { name: "Principle of Calorimetry (Ice-Water Phase Changes)", probability: "78%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Junction temperature calculation for rods of different conductivities connected end-to-end."
      },
      {
        id: "jee-phy-11-11",
        chapter: "Thermodynamics & Kinetic Theory of Gases (KTG)",
        classLevel: "11th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "96%",
        recentTrend: [2, 3, 2, 3, 3],
        isLocked: true,
        subTopics: [
          { name: "First Law of Thermodynamics & P-V Cyclic Work", probability: "92%", primaryType: "Numerical (80%)" },
          { name: "Adiabatic & Polytropic Processes ($PV^γ = C$)", probability: "88%", primaryType: "Single Correct (75%)" },
          { name: "Degrees of Freedom & Molar Heat Capacities of Mixtures", probability: "86%", primaryType: "Numerical (75%)" }
        ],
        predictionNote: "Direct efficiency and total work done calculations from cyclic indicator diagrams."
      },
      {
        id: "jee-phy-11-12",
        chapter: "Simple Harmonic Motion (Oscillations)",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "89%",
        recentTrend: [1, 2, 2, 1, 2],
        isLocked: true,
        subTopics: [
          { name: "Spring-Block Combinations & Effective Time Period", probability: "88%", primaryType: "Numerical (80%)" },
          { name: "Energy in SHM (Kinetic vs Potential Variations)", probability: "82%", primaryType: "Single Correct (70%)" },
          { name: "Superposition of Two Perpendicular SHMs", probability: "74%", primaryType: "Single Correct (60%)" }
        ],
        predictionNote: "Time period of physical pendulums and submerged oscillating cylinders."
      },
      {
        id: "jee-phy-11-13",
        chapter: "Waves & Sound Waves",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "88%",
        recentTrend: [1, 2, 1, 2, 2],
        isLocked: true,
        subTopics: [
          { name: "Standing Waves in Organ Pipes & Resonance Columns", probability: "90%", primaryType: "Numerical (85%)" },
          { name: "Beats Frequency & Tuning Fork Loading Problems", probability: "84%", primaryType: "Numerical (75%)" },
          { name: "Wave Equation Phase Difference & Intensity", probability: "78%", primaryType: "Single Correct (65%)" }
        ],
        predictionNote: "End corrections in closed/open organ pipes with fundamental frequency harmonics."
      },
      // Class 12th
      {
        id: "jee-phy-12-01",
        chapter: "Electrostatics & Gauss's Law",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "94%",
        recentTrend: [2, 2, 2, 3, 2],
        isLocked: true,
        subTopics: [
          { name: "Gauss's Law Electric Flux Calculations", probability: "90%", primaryType: "Numerical (80%)" },
          { name: "Electric Potential of Non-Uniformly Charged Shells/Spheres", probability: "85%", primaryType: "Single Correct (70%)" },
          { name: "Electric Dipole Torque & Field on Axial/Equatorial Planes", probability: "80%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Non-conducting spherical distributions with radial charge density $\\rho(r) = kr$."
      },
      {
        id: "jee-phy-12-02",
        chapter: "Capacitors & Dielectrics",
        classLevel: "12th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "92%",
        recentTrend: [1, 2, 1, 2, 2],
        isLocked: true,
        subTopics: [
          { name: "Dielectric Slabs & Variable Dielectric Constants", probability: "92%", primaryType: "Numerical (85%)" },
          { name: "Energy Redistribution & Loss on Capacitor Connecting", probability: "86%", primaryType: "Numerical (80%)" },
          { name: "RC Circuit Charging/Discharging Time Constants", probability: "80%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Capacitance when dielectric constant varies linearly $K(x) = K_0 + ax$ across plates."
      },
      {
        id: "jee-phy-12-03",
        chapter: "Current Electricity",
        classLevel: "12th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "98%",
        recentTrend: [2, 3, 2, 3, 3],
        isLocked: false,
        subTopics: [
          { name: "Kirchhoff's Laws & Symmetry Cube/Bridge Networks", probability: "90%", primaryType: "Numerical (80%)" },
          { name: "Galvanometer to Ammeter/Voltmeter Shunt Calculations", probability: "88%", primaryType: "Numerical (85%)" },
          { name: "Drift Velocity & Temperature Coefficient of Resistance", probability: "82%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Shunt resistance value calculations for high-accuracy galvanometer conversions."
      },
      {
        id: "jee-phy-12-04",
        chapter: "Magnetic Effects of Current & Moving Charges",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "92%",
        recentTrend: [2, 2, 2, 2, 3],
        isLocked: true,
        subTopics: [
          { name: "Biot-Savart Law for Circular Arcs and Straight Wires", probability: "88%", primaryType: "Single Correct (75%)" },
          { name: "Charged Particle Helical Trajectory in Crossed Fields", probability: "84%", primaryType: "Numerical (70%)" },
          { name: "Ampere's Circuital Law for Cylindrical Conductors", probability: "80%", primaryType: "Single Correct (65%)" }
        ],
        predictionNote: "Superposition of magnetic fields at origin due to combination of semi-infinite wires and arcs."
      },
      {
        id: "jee-phy-12-05",
        chapter: "Magnetism & Matter",
        classLevel: "12th",
        avgQuestions: 1.0,
        avgMarks: 4,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "85%",
        recentTrend: [1, 1, 1, 1, 1],
        isLocked: true,
        subTopics: [
          { name: "Magnetic Susceptibility ($χ$) & Curie's Law", probability: "86%", primaryType: "Matching (80%)" },
          { name: "Magnetic Dipole in Uniform Field & Oscillation Time", probability: "82%", primaryType: "Numerical (75%)" },
          { name: "Dia, Para & Ferromagnetic Material Properties", probability: "80%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Direct qualitative matching questions on magnetic properties from NCERT."
      },
      {
        id: "jee-phy-12-06",
        chapter: "Electromagnetic Induction (EMI)",
        classLevel: "12th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "90%",
        recentTrend: [1, 2, 1, 2, 2],
        isLocked: true,
        subTopics: [
          { name: "Motional EMF on Rotating and Sliding Rods in B-Field", probability: "90%", primaryType: "Numerical (80%)" },
          { name: "Self & Mutual Inductance Configurations", probability: "82%", primaryType: "Numerical (75%)" },
          { name: "Lenz's Law & Induced Current Directions", probability: "80%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "A conductor sliding on frictionless rails with magnetic field and resistance power dissipation."
      },
      {
        id: "jee-phy-12-07",
        chapter: "Alternating Current (AC)",
        classLevel: "12th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "93%",
        recentTrend: [1, 2, 2, 1, 2],
        isLocked: true,
        subTopics: [
          { name: "Series LCR Circuits Impedance & Resonance", probability: "94%", primaryType: "Numerical (85%)" },
          { name: "Quality Factor (Q) & Power Factor ($cos\\,\\phi$)", probability: "88%", primaryType: "Numerical (80%)" },
          { name: "RMS vs Peak Values and Phasor Diagrams", probability: "80%", primaryType: "Single Correct (65%)" }
        ],
        predictionNote: "Resonant frequency and sharp bandwidth calculations in series R-L-C circuits."
      },
      {
        id: "jee-phy-12-08",
        chapter: "Electromagnetic Waves",
        classLevel: "12th",
        avgQuestions: 1.0,
        avgMarks: 4,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "92%",
        recentTrend: [1, 1, 1, 1, 1],
        isLocked: true,
        subTopics: [
          { name: "Displacement Current & Maxwell Equations", probability: "88%", primaryType: "Numerical (75%)" },
          { name: "Relations between $E_0, B_0$ and Wave Propagation Vector", probability: "92%", primaryType: "Single Correct (85%)" },
          { name: "Electromagnetic Spectrum Applications & Wavelengths", probability: "85%", primaryType: "Matching (80%)" }
        ],
        predictionNote: "Vector cross product direction relation $\\vec{k} = \\vec{E} \\times \\vec{B}$."
      },
      {
        id: "jee-phy-12-09",
        chapter: "Ray Optics & Optical Instruments",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "94%",
        recentTrend: [2, 2, 2, 3, 2],
        isLocked: true,
        subTopics: [
          { name: "Lens Maker's Formula & Cutting/Combining Lenses", probability: "90%", primaryType: "Numerical (80%)" },
          { name: "Prism Refraction & Minimum Angle of Deviation", probability: "86%", primaryType: "Single Correct (75%)" },
          { name: "Compound Microscope & Astronomical Telescope Magnification", probability: "82%", primaryType: "Numerical (75%)" }
        ],
        predictionNote: "Lens immersed in a liquid medium changing its focal length and convergent/divergent behavior."
      },
      {
        id: "jee-phy-12-10",
        chapter: "Wave Optics",
        classLevel: "12th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "90%",
        recentTrend: [1, 2, 1, 2, 2],
        isLocked: true,
        subTopics: [
          { name: "YDSE Fringe Shift due to Thin Transparent Slab Insertion", probability: "92%", primaryType: "Numerical (85%)" },
          { name: "Interference Maxima/Minima Conditions & Resultant Intensity", probability: "86%", primaryType: "Single Correct (70%)" },
          { name: "Diffraction Central Maxima Width at Single Slit", probability: "80%", primaryType: "Numerical (75%)" }
        ],
        predictionNote: "Shift of central fringe upon placing a glass sheet of thickness 't' and refractive index 'μ'."
      },
      {
        id: "jee-phy-12-11",
        chapter: "Dual Nature of Radiation & Matter (Modern Physics 1)",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "98%",
        recentTrend: [2, 2, 2, 3, 2],
        isLocked: false,
        subTopics: [
          { name: "Photoelectric Equation & Stopping Potential Graphs", probability: "95%", primaryType: "Numerical (85%)" },
          { name: "de Broglie Wavelength of Charged Particles vs Potential", probability: "92%", primaryType: "Numerical (80%)" }
        ],
        predictionNote: "Slope and intercept of stopping potential vs incident frequency graphs."
      },
      {
        id: "jee-phy-12-12",
        chapter: "Atoms & Nuclei (Modern Physics 2)",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "97%",
        recentTrend: [2, 2, 2, 2, 3],
        isLocked: true,
        subTopics: [
          { name: "Bohr Model Energy Levels, Radii & Spectral Transitions", probability: "94%", primaryType: "Numerical (85%)" },
          { name: "Mass Defect, $Q$-value & Binding Energy Curve", probability: "88%", primaryType: "Numerical (80%)" }
        ],
        predictionNote: "Ratio of shortest to longest wavelength in Lyman vs Balmer series."
      },
      {
        id: "jee-phy-12-13",
        chapter: "Semiconductors & Electronic Devices",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "96%",
        recentTrend: [2, 2, 2, 2, 3],
        isLocked: true,
        subTopics: [
          { name: "Logic Gates Combinations & Boolean Reduction", probability: "95%", primaryType: "Matching/MCQ (90%)" },
          { name: "Zener Diode Voltage Regulator Circuit Calculations", probability: "88%", primaryType: "Numerical (80%)" },
          { name: "p-n Junction Diode Forward & Reverse Characteristic Curves", probability: "82%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Guaranteed 1 question evaluating the truth table output 'Y' of interconnected NAND/NOR gates."
      }
    ],
    chemistry: [
      // Class 11th Chemistry
      {
        id: "jee-chem-11-01",
        chapter: "Some Basic Concepts of Chemistry (Mole Concept)",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "92%",
        recentTrend: [1, 2, 1, 2, 2],
        isLocked: false,
        subTopics: [
          { name: "Limiting Reagent & Stoichiometric Yields", probability: "92%", primaryType: "Numerical (85%)" },
          { name: "Concentration Terms (Molarity, Molality, Mole Fraction, ppm)", probability: "90%", primaryType: "Numerical (80%)" },
          { name: "Empirical & Molecular Formula Determination", probability: "78%", primaryType: "Single Correct (65%)" }
        ],
        predictionNote: "Questions mixing molarity and molality using density conversions of solutions."
      },
      {
        id: "jee-chem-11-02",
        chapter: "Structure of Atom",
        classLevel: "11th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "95%",
        recentTrend: [2, 2, 2, 2, 3],
        isLocked: false,
        subTopics: [
          { name: "Quantum Numbers & Electronic Configurations (Exceptions)", probability: "92%", primaryType: "Single Correct (85%)" },
          { name: "Radial & Angular Nodes Formula Calculations", probability: "88%", primaryType: "Numerical (80%)" },
          { name: "Photoelectric & Bohr's Hydrogen Atom Transitions", probability: "85%", primaryType: "Numerical (75%)" }
        ],
        predictionNote: "Radial node counting for $3d, 4p, 5s$ orbitals using formula $(n - l - 1)$."
      },
      {
        id: "jee-chem-11-03",
        chapter: "Classification of Elements & Periodicity",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "94%",
        recentTrend: [1, 2, 2, 1, 2],
        isLocked: true,
        subTopics: [
          { name: "Ionization Enthalpy Anomalies (Be vs B, N vs O)", probability: "94%", primaryType: "Ordering MCQ (85%)" },
          { name: "Electron Gain Enthalpy Trends (Cl vs F, O vs S)", probability: "90%", primaryType: "Single Correct (80%)" },
          { name: "Ionic Radii of Isoelectronic Species", probability: "86%", primaryType: "Matching (75%)" }
        ],
        predictionNote: "Correct increasing order of 1st and 2nd ionization enthalpies across period 2 and 3."
      },
      {
        id: "jee-chem-11-04",
        chapter: "Chemical Bonding & Molecular Structure",
        classLevel: "11th",
        avgQuestions: 3.0,
        avgMarks: 12,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "99%",
        recentTrend: [3, 3, 3, 3, 4],
        isLocked: true,
        subTopics: [
          { name: "VSEPR Geometry & Lone Pair Counting ($XeF_4, SF_4, ClF_3$)", probability: "96%", primaryType: "Matching (85%)" },
          { name: "Molecular Orbital Theory (MOT) Bond Order & Magnetism", probability: "95%", primaryType: "Assertion-Reason (80%)" },
          { name: "Dipole Moment & Fajan's Rules for Covalent Character", probability: "84%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Paramagnetic/Diamagnetic character of 14-18 electron diatomic ions like $O_2^{2-}, N_2^+, C_2$."
      },
      {
        id: "jee-chem-11-05",
        chapter: "Chemical Thermodynamics & Energetics",
        classLevel: "11th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "93%",
        recentTrend: [2, 2, 2, 3, 2],
        isLocked: true,
        subTopics: [
          { name: "Gibbs Free Energy ($\Delta G = \Delta H - T\Delta S$) & Spontaneity", probability: "92%", primaryType: "Numerical (80%)" },
          { name: "Hess's Law & Standard Enthalpies of Combustion/Formation", probability: "86%", primaryType: "Numerical (85%)" },
          { name: "Isothermal vs Adiabatic Reversible Work Done", probability: "80%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Calculating temperature at which reaction switches from non-spontaneous to spontaneous."
      },
      {
        id: "jee-chem-11-06",
        chapter: "Chemical & Ionic Equilibrium",
        classLevel: "11th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "95%",
        recentTrend: [2, 3, 2, 3, 3],
        isLocked: true,
        subTopics: [
          { name: "Buffer Solutions & Henderson-Hasselbalch Equation pH", probability: "90%", primaryType: "Numerical (85%)" },
          { name: "Solubility Product ($K_{sp}$) in Presence of Common Ion", probability: "88%", primaryType: "Numerical (85%)" },
          { name: "Le Chatelier's Principle & $K_p / K_c$ Transformations", probability: "82%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "pH calculation of mixed weak acid + strong base partial neutralization buffer."
      },
      {
        id: "jee-chem-11-07",
        chapter: "Redox Reactions",
        classLevel: "11th",
        avgQuestions: 1.0,
        avgMarks: 4,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "88%",
        recentTrend: [1, 1, 1, 1, 1],
        isLocked: true,
        subTopics: [
          { name: "Balancing Redox Equations in Acidic/Basic Media", probability: "90%", primaryType: "Numerical (80%)" },
          { name: "Oxidation Number Exceptions ($CrO_5, H_2SO_5, CaOCl_2$)", probability: "88%", primaryType: "Single Correct (85%)" }
        ],
        predictionNote: "n-factor calculations for disproportionation reactions."
      },
      {
        id: "jee-chem-11-08",
        chapter: "General Organic Chemistry (GOC) & Electronic Effects",
        classLevel: "11th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "98%",
        recentTrend: [2, 3, 3, 2, 3],
        isLocked: true,
        subTopics: [
          { name: "Acidic Strength of Phenols & Carboxylic Acids (Ortho Effect)", probability: "95%", primaryType: "Ordering MCQ (90%)" },
          { name: "Aromaticity (Hückel's Rule, Non-aromatic, Anti-aromatic)", probability: "92%", primaryType: "Matching (80%)" },
          { name: "Carbocation & Carbon Radical Stabilities (Hyperconjugation)", probability: "88%", primaryType: "Single Correct (75%)" }
        ],
        predictionNote: "Ordering of substituted aromatic amine basicities in aqueous vs non-polar solvents."
      },
      {
        id: "jee-chem-11-09",
        chapter: "Hydrocarbons (Alkanes, Alkenes, Alkynes & Arenes)",
        classLevel: "11th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "92%",
        recentTrend: [2, 2, 2, 2, 3],
        isLocked: true,
        subTopics: [
          { name: "Electrophilic Addition to Alkenes (Markownikoff & Anti-Mark)", probability: "90%", primaryType: "Reaction Mechanism MCQ (80%)" },
          { name: "Ozonolysis ($O_3 / Zn$) Product Identification", probability: "88%", primaryType: "Single Correct (85%)" },
          { name: "Electrophilic Aromatic Substitution (Nitration, Friedel-Crafts)", probability: "85%", primaryType: "Single Correct (75%)" }
        ],
        predictionNote: "Reductive vs Oxidative ozonolysis of symmetrical and branched polyenes."
      },
      {
        id: "jee-chem-11-10",
        chapter: "Purification & Characterization of Organic Compounds",
        classLevel: "11th",
        avgQuestions: 1.0,
        avgMarks: 4,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "86%",
        recentTrend: [1, 1, 1, 1, 1],
        isLocked: true,
        subTopics: [
          { name: "Kjeldahl & Dumas Method Nitrogen Estimation", probability: "90%", primaryType: "Numerical (85%)" },
          { name: "Lassaigne's Test for N, S, Halogens", probability: "84%", primaryType: "Matching (75%)" }
        ],
        predictionNote: "Direct percentage of nitrogen numerical from Kjeldahl standard acid neutralization."
      },
      // Class 12th Chemistry
      {
        id: "jee-chem-12-01",
        chapter: "Solutions & Colligative Properties",
        classLevel: "12th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "94%",
        recentTrend: [1, 2, 2, 1, 2],
        isLocked: true,
        subTopics: [
          { name: "van 't Hoff Factor (i) with Degree of Association/Dissociation", probability: "92%", primaryType: "Numerical (85%)" },
          { name: "Depression in Freezing Point & Osmotic Pressure", probability: "88%", primaryType: "Numerical (80%)" },
          { name: "Raoult's Law & Positive/Negative Azeotropes", probability: "80%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Abnormal molar mass computation for dimerizing acids in benzene."
      },
      {
        id: "jee-chem-12-02",
        chapter: "Electrochemistry",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "95%",
        recentTrend: [2, 2, 2, 3, 2],
        isLocked: true,
        subTopics: [
          { name: "Nernst Equation for Cell Potential and Equilibrium Constant", probability: "92%", primaryType: "Numerical (85%)" },
          { name: "Kohlrausch's Law of Independent Migration of Ions", probability: "86%", primaryType: "Numerical (80%)" },
          { name: "Faraday's Laws of Electrolysis", probability: "80%", primaryType: "Numerical (75%)" }
        ],
        predictionNote: "EMF calculations with non-standard concentration half-cells and sparingly soluble salts."
      },
      {
        id: "jee-chem-12-03",
        chapter: "Chemical Kinetics",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "95%",
        recentTrend: [2, 2, 2, 2, 2],
        isLocked: true,
        subTopics: [
          { name: "First Order Kinetics Rate Law & Half-Life Relationships", probability: "94%", primaryType: "Numerical (85%)" },
          { name: "Arrhenius Equation Activation Energy Slope ($\ln k$ vs $1/T$)", probability: "88%", primaryType: "Numerical (80%)" },
          { name: "Order of Reaction from Initial Rates Method", probability: "82%", primaryType: "Numerical (75%)" }
        ],
        predictionNote: "Fractional decomposition time calculations for consecutive 1st order reactions."
      },
      {
        id: "jee-chem-12-04",
        chapter: "d and f Block Elements",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "92%",
        recentTrend: [2, 2, 2, 2, 2],
        isLocked: true,
        subTopics: [
          { name: "Potassium Permanganate ($KMnO_4$) Redox Titrations", probability: "90%", primaryType: "Numerical/MCQ (80%)" },
          { name: "Potassium Dichromate ($K_2Cr_2O_7$) Reactions & Chromyl Chloride Test", probability: "86%", primaryType: "Matching (80%)" },
          { name: "Lanthanoid Contraction & Magnetic Properties", probability: "84%", primaryType: "Assertion-Reason (75%)" }
        ],
        predictionNote: "Oxidation states and color changes in acidic, alkaline and neutral media."
      },
      {
        id: "jee-chem-12-05",
        chapter: "Coordination Compounds",
        classLevel: "12th",
        avgQuestions: 3.0,
        avgMarks: 12,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "98%",
        recentTrend: [3, 3, 3, 3, 4],
        isLocked: false,
        subTopics: [
          { name: "Crystal Field Theory (CFSE & Pairing Energy Comparisons)", probability: "94%", primaryType: "Single Correct (85%)" },
          { name: "Geometrical & Optical Isomerism Counting", probability: "90%", primaryType: "Numerical/Count (80%)" },
          { name: "IUPAC Nomenclature & Spin-Only Magnetic Moments", probability: "88%", primaryType: "Numerical (85%)" }
        ],
        predictionNote: "Counting total stereoisomers (optical + geometrical) for $[M(AA)_2b_2]$ and $[Mabcd]$."
      },
      {
        id: "jee-chem-12-06",
        chapter: "Haloalkanes & Haloarenes",
        classLevel: "12th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "90%",
        recentTrend: [1, 2, 1, 2, 2],
        isLocked: true,
        subTopics: [
          { name: "$S_N1$ vs $S_N2$ Reactivity Kinetics & Stereochemistry Inversion", probability: "92%", primaryType: "Single Correct (85%)" },
          { name: "Elimination Reactions (Saytzeff vs Hofmann)", probability: "85%", primaryType: "Reaction Mechanism MCQ (80%)" },
          { name: "Sandmeyer Reaction & Aryl Halide Nucleophilic Substitution", probability: "78%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Chirality retention vs inversion in $S_N2$ substitution on chiral alkyl halides."
      },
      {
        id: "jee-chem-12-07",
        chapter: "Alcohols, Phenols & Ethers",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "93%",
        recentTrend: [2, 2, 2, 2, 3],
        isLocked: true,
        subTopics: [
          { name: "Reimer-Tiemann & Kolbe's Reaction of Phenols", probability: "90%", primaryType: "Single Correct (85%)" },
          { name: "Williamson's Ether Synthesis & HI Cleavage of Unsymmetrical Ethers", probability: "92%", primaryType: "Reaction Mechanism MCQ (80%)" },
          { name: "Lucas Reagent Test ($ZnCl_2 / HCl$) for Alcohols", probability: "82%", primaryType: "Matching (75%)" }
        ],
        predictionNote: "Cleavage of ethers containing $3^\\circ$ alkyl or benzyl groups with concentrated HI."
      },
      {
        id: "jee-chem-12-08",
        chapter: "Aldehydes, Ketones & Carboxylic Acids",
        classLevel: "12th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "96%",
        recentTrend: [2, 3, 2, 3, 3],
        isLocked: true,
        subTopics: [
          { name: "Aldol & Cross-Aldol Condensations Mechanism", probability: "94%", primaryType: "Reaction Mechanism MCQ (85%)" },
          { name: "Cannizzaro Reaction & Iodoform Test Positive Compounds", probability: "90%", primaryType: "Numerical/Count (80%)" },
          { name: "HVZ Reaction & Acid Derivative Reactivity Orders", probability: "82%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Count of compounds giving positive iodoform and 2,4-DNP tests in a given series."
      },
      {
        id: "jee-chem-12-09",
        chapter: "Amines & Diazonium Salts",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "94%",
        recentTrend: [2, 2, 2, 2, 2],
        isLocked: true,
        subTopics: [
          { name: "Hoffmann Bromamide Degradation & Carbylamine Test", probability: "92%", primaryType: "Single Correct (85%)" },
          { name: "Diazonium Salt Coupling Reactions (Azo Dye Formations)", probability: "88%", primaryType: "Matching (80%)" },
          { name: "Hinsberg's Reagent Test for $1^\\circ, 2^\\circ, 3^\\circ$ Amines", probability: "84%", primaryType: "Single Correct (75%)" }
        ],
        predictionNote: "Multi-step conversion of nitrobenzene to substituted aniline through diazonium salts."
      },
      {
        id: "jee-chem-12-10",
        chapter: "Biomolecules",
        classLevel: "12th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "95%",
        recentTrend: [1, 2, 2, 1, 2],
        isLocked: true,
        subTopics: [
          { name: "Glucose Structure (Haworth Form & Mutarotation Reactions)", probability: "92%", primaryType: "Single Correct (85%)" },
          { name: "Amino Acids (Essential vs Non-Essential, Isoelectric Point)", probability: "90%", primaryType: "Matching (85%)" },
          { name: "Nucleic Acids (Purine vs Pyrimidine Bases, Phosphodiester Bonds)", probability: "86%", primaryType: "Direct MCQ (80%)" }
        ],
        predictionNote: "Direct matching of amino acid structures with their specific side-chain functional groups."
      }
    ],
    math: [
      // Class 11th Math
      {
        id: "jee-math-11-01",
        chapter: "Sets, Relations & Functions",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "92%",
        recentTrend: [1, 2, 1, 2, 2],
        isLocked: false,
        subTopics: [
          { name: "Equivalence Relations (Reflexive, Symmetric, Transitive)", probability: "90%", primaryType: "Single Correct (80%)" },
          { name: "Domain & Range of Composite & Inverse Functions", probability: "86%", primaryType: "Numerical (75%)" },
          { name: "One-One (Injective) & Onto (Surjective) Counting", probability: "82%", primaryType: "Numerical (75%)" }
        ],
        predictionNote: "Number of equivalence relations containing specific ordered pairs."
      },
      {
        id: "jee-math-11-02",
        chapter: "Complex Numbers & Quadratic Equations",
        classLevel: "11th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "94%",
        recentTrend: [2, 2, 2, 2, 3],
        isLocked: false,
        subTopics: [
          { name: "Newton's Sum Formula for Power of Quadratic Roots ($\alpha^n + \beta^n$)", probability: "92%", primaryType: "Numerical (85%)" },
          { name: "Geometrical Loci of Complex Numbers ($|z - z_1|/|z - z_2| = k$)", probability: "85%", primaryType: "Single Correct (75%)" },
          { name: "Cube Roots of Unity ($\omega, \omega^2$) Algebraic Identities", probability: "80%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Application of Newton's recurrence formula to evaluate higher degree root equations."
      },
      {
        id: "jee-math-11-03",
        chapter: "Permutations & Combinations (PnC)",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "Moderate Yield / Moderate Effort",
        repeatProbability: "90%",
        recentTrend: [1, 2, 1, 2, 2],
        isLocked: true,
        subTopics: [
          { name: "Distribution of Identical Objects into Distinct Boxes", probability: "88%", primaryType: "Numerical (80%)" },
          { name: "Circular Permutations & Rank of Word in Dictionary", probability: "84%", primaryType: "Numerical (75%)" },
          { name: "Dearrangement Formula ($D_n$)", probability: "76%", primaryType: "Single Correct (65%)" }
        ],
        predictionNote: "Rank of word with repeated letters in dictionary order."
      },
      {
        id: "jee-math-11-04",
        chapter: "Binomial Theorem",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "93%",
        recentTrend: [2, 1, 2, 2, 2],
        isLocked: true,
        subTopics: [
          { name: "Remainder & Divisibility Proofs Using Binomial Expansion", probability: "92%", primaryType: "Numerical (90%)" },
          { name: "Term Independent of 'x' & General Middle Terms", probability: "88%", primaryType: "Single Correct (75%)" },
          { name: "Series Sum of Binomial Coefficients ($^nC_r$ Properties)", probability: "80%", primaryType: "Numerical (75%)" }
        ],
        predictionNote: "Finding integer remainder when $7^{2026}$ or $a^n$ is divided by $(a-1)^2$."
      },
      {
        id: "jee-math-11-05",
        chapter: "Sequences & Series (AP, GP & Special Series)",
        classLevel: "11th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "95%",
        recentTrend: [2, 2, 2, 2, 3],
        isLocked: true,
        subTopics: [
          { name: "$V_n$ Method (Telescoping Series Partial Fractions)", probability: "90%", primaryType: "Numerical (85%)" },
          { name: "Arithmetico-Geometric Progression (AGP) Summation", probability: "86%", primaryType: "Numerical (80%)" },
          { name: "AM-GM-HM Inequalities for Range Optimization", probability: "82%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Telescoping summation with cubic/quadratic polynomial roots in denominator."
      },
      {
        id: "jee-math-11-06",
        chapter: "Straight Lines & Pair of Straight Lines",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "90%",
        recentTrend: [1, 2, 1, 2, 2],
        isLocked: true,
        subTopics: [
          { name: "Orthocenter, Centroid & Circumcenter Coordinates", probability: "88%", primaryType: "Numerical (80%)" },
          { name: "Distance Between Parallel Lines & Foot of Perpendicular", probability: "84%", primaryType: "Single Correct (75%)" },
          { name: "Family of Lines Passing Through Intersection Point ($L_1 + \lambda L_2 = 0$)", probability: "80%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Euler line properties: centroid dividing orthocenter-circumcenter in 2:1 ratio."
      },
      {
        id: "jee-math-11-07",
        chapter: "Circles & System of Circles",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "91%",
        recentTrend: [1, 2, 2, 1, 2],
        isLocked: true,
        subTopics: [
          { name: "Common Tangents (Direct & Transverse) & Lengths", probability: "90%", primaryType: "Numerical (85%)" },
          { name: "Orthogonal Intersection of Two Circles Condition", probability: "82%", primaryType: "Single Correct (70%)" },
          { name: "Chord of Contact ($T=0$) & Power of Point", probability: "80%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Number of common tangents between two circles with variable centers."
      },
      {
        id: "jee-math-11-08",
        chapter: "Conic Sections (Parabola, Ellipse, Hyperbola)",
        classLevel: "11th",
        avgQuestions: 3.0,
        avgMarks: 12,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "96%",
        recentTrend: [3, 3, 3, 3, 4],
        isLocked: true,
        subTopics: [
          { name: "Common Tangent Conditions Between Parabola & Ellipse/Hyperbola", probability: "94%", primaryType: "Numerical (85%)" },
          { name: "Eccentricity, Foci & Directrix Equations of Conics", probability: "88%", primaryType: "Single Correct (75%)" },
          { name: "Parametric Normal & Tangent Equations", probability: "82%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Slope form tangent conditions for simultaneous contact with $y^2 = 4ax$ and $x^2/a^2 + y^2/b^2 = 1$."
      },
      {
        id: "jee-math-11-09",
        chapter: "Trigonometric Ratios, Identities & Equations",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "Moderate Yield / Moderate Effort",
        repeatProbability: "88%",
        recentTrend: [1, 2, 1, 2, 1],
        isLocked: true,
        subTopics: [
          { name: "Trigonometric Series Telescopic Product ($\cos x \cos 2x \cos 4x$)", probability: "85%", primaryType: "Numerical (75%)" },
          { name: "General Solution of Trigonometric Equations", probability: "80%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Number of solutions in a closed interval like $[0, 4\pi]$."
      },
      {
        id: "jee-math-11-10",
        chapter: "Statistics & Measures of Dispersion",
        classLevel: "11th",
        avgQuestions: 1.0,
        avgMarks: 4,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "95%",
        recentTrend: [1, 1, 1, 1, 1],
        isLocked: true,
        subTopics: [
          { name: "Variance & Standard Deviation Calculations for Shifted Data", probability: "95%", primaryType: "Numerical (90%)" },
          { name: "Incorrect Observation Correction in Mean & Variance", probability: "90%", primaryType: "Numerical (85%)" }
        ],
        predictionNote: "Calculating new variance after replacing 2 erroneous data entries."
      },
      // Class 12th Math
      {
        id: "jee-math-12-01",
        chapter: "Matrices & Determinants",
        classLevel: "12th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "98%",
        recentTrend: [2, 3, 2, 3, 3],
        isLocked: false,
        subTopics: [
          { name: "System of Linear Equations (Cramer's Rule Infinite/No Solutions)", probability: "96%", primaryType: "Numerical (90%)" },
          { name: "Adjoint Properties ($|\\text{adj } A| = |A|^{n-1}$)", probability: "90%", primaryType: "Single Correct (80%)" },
          { name: "Symmetric & Skew-Symmetric Matrix Properties", probability: "80%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Conditions on $\lambda, \mu$ for infinite solutions ($\Delta = 0, \Delta_x = 0$)."
      },
      {
        id: "jee-math-12-02",
        chapter: "Limits, Continuity & Differentiability",
        classLevel: "12th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "96%",
        recentTrend: [2, 3, 2, 3, 3],
        isLocked: true,
        subTopics: [
          { name: "$1^\\infty$ Indeterminate Form Standard Reductions", probability: "92%", primaryType: "Numerical (85%)" },
          { name: "Counting Non-Differentiable Points of Modulus Functions", probability: "90%", primaryType: "Numerical (80%)" },
          { name: "L'Hôpital's Rule & Expansion Series Limits", probability: "86%", primaryType: "Single Correct (75%)" }
        ],
        predictionNote: "Number of non-differentiable sharp turn points for $f(x) = \max(|x^2 - 4|, |x - 1|)$."
      },
      {
        id: "jee-math-12-03",
        chapter: "Application of Derivatives (AOD)",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "94%",
        recentTrend: [2, 2, 2, 3, 2],
        isLocked: true,
        subTopics: [
          { name: "Maxima & Minima Optimization Problems", probability: "92%", primaryType: "Numerical (85%)" },
          { name: "Monotonicity & Increasing/Decreasing Intervals", probability: "86%", primaryType: "Single Correct (75%)" },
          { name: "Rolle's & Lagrange's Mean Value Theorems (LMVT)", probability: "80%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Real root counting using sign changes of derivative function $f'(x)$."
      },
      {
        id: "jee-math-12-04",
        chapter: "Indefinite & Definite Integration",
        classLevel: "12th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "96%",
        recentTrend: [2, 3, 3, 2, 3],
        isLocked: true,
        subTopics: [
          { name: "King's Property: $\int_a^b f(x)dx = \int_a^b f(a+b-x)dx$", probability: "95%", primaryType: "Single Correct (85%)" },
          { name: "Leibniz Integral Rule for Differentiating Limits", probability: "92%", primaryType: "Numerical (85%)" },
          { name: "Definite Integral as Limit of a Sum ($\Sigma \to \int$)", probability: "84%", primaryType: "Single Correct (75%)" }
        ],
        predictionNote: "Evaluating $\lim_{x \to 0} \frac{\int_0^{x^2} \sin\sqrt{t}\,dt}{x^3}$ using Leibniz rule."
      },
      {
        id: "jee-math-12-05",
        chapter: "Differential Equations",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "95%",
        recentTrend: [2, 2, 2, 2, 3],
        isLocked: true,
        subTopics: [
          { name: "Linear Differential Equations ($dy/dx + Py = Q$) with IF", probability: "94%", primaryType: "Numerical (90%)" },
          { name: "Homogeneous Differential Equations ($y = vx$ substitution)", probability: "82%", primaryType: "Single Correct (75%)" },
          { name: "Exact Differential Forms ($d(xy), d(x/y)$)", probability: "80%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Finding value of $y(\pi/2)$ given initial condition $y(0) = 1$ in linear differential format."
      },
      {
        id: "jee-math-12-06",
        chapter: "Area Under Curves",
        classLevel: "12th",
        avgQuestions: 1.0,
        avgMarks: 4,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "94%",
        recentTrend: [1, 1, 1, 1, 1],
        isLocked: true,
        subTopics: [
          { name: "Area Between Parabola and Line/Modulus Curve", probability: "92%", primaryType: "Numerical (85%)" },
          { name: "Area Bounded by Inverse Trigonometric/Exponential Curves", probability: "80%", primaryType: "Single Correct (75%)" }
        ],
        predictionNote: "Area bounded by region $\{(x,y): y^2 \le 4x, x+y \le 3, x \ge 0\}$."
      },
      {
        id: "jee-math-12-07",
        chapter: "Vector Algebra",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "96%",
        recentTrend: [2, 2, 2, 2, 3],
        isLocked: false,
        subTopics: [
          { name: "Scalar Triple Product & Coplanarity ($[\\vec{a}\\,\\vec{b}\\,\\vec{c}] = 0$)", probability: "92%", primaryType: "Numerical (85%)" },
          { name: "Vector Triple Product Expansion Identities", probability: "88%", primaryType: "Single Correct (75%)" },
          { name: "Projection of Vector along Line and Angle Bisectors", probability: "82%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Volume of parallelepiped formed by coterminous vector edges."
      },
      {
        id: "jee-math-12-08",
        chapter: "Three Dimensional Geometry (3D)",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "98%",
        recentTrend: [2, 2, 3, 2, 3],
        isLocked: false,
        subTopics: [
          { name: "Shortest Distance Between Two Skew Lines", probability: "95%", primaryType: "Numerical (90%)" },
          { name: "Foot of Perpendicular & Mirror Image of a Point in Line", probability: "90%", primaryType: "Numerical (85%)" },
          { name: "Line of Intersection of Two Surfaces", probability: "80%", primaryType: "Single Correct (70%)" }
        ],
        predictionNote: "Standard shortest distance formula between two parameterized vector equations of lines."
      },
      {
        id: "jee-math-12-09",
        chapter: "Probability & Bayes' Theorem",
        classLevel: "12th",
        avgQuestions: 2.0,
        avgMarks: 8,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "95%",
        recentTrend: [2, 2, 2, 2, 2],
        isLocked: true,
        subTopics: [
          { name: "Bayes' Rule & Total Probability Theorem", probability: "92%", primaryType: "Numerical (85%)" },
          { name: "Independent Events & Conditional Probability", probability: "88%", primaryType: "Single Correct (75%)" },
          { name: "Probability Distribution & Expectation (Mean/Variance)", probability: "82%", primaryType: "Numerical (80%)" }
        ],
        predictionNote: "Bayes' theorem problems involving urn selection with black and white ball transfers."
      }
    ]
  },
  neet: {
    biology: [
      // Class 11th Biology
      {
        id: "neet-bio-11-01",
        chapter: "The Living World",
        classLevel: "11th",
        avgQuestions: 1.5,
        avgMarks: 6,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "88%",
        recentTrend: [1, 2, 1, 1, 2],
        isLocked: false,
        subTopics: [
          { name: "Taxonomic Hierarchy (Species, Genus, Order, Family)", probability: "90%", primaryType: "Matching (85%)" },
          { name: "Universal Rules of Binomial Nomenclature", probability: "85%", primaryType: "Direct NCERT (80%)" }
        ],
        predictionNote: "Matching table for scientific names: Homo sapiens, Musca domestica, Mangifera indica."
      },
      {
        id: "neet-bio-11-02",
        chapter: "Biological Classification",
        classLevel: "11th",
        avgQuestions: 3.5,
        avgMarks: 14,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "97%",
        recentTrend: [3, 4, 3, 4, 4],
        isLocked: false,
        subTopics: [
          { name: "Kingdom Fungi (Phyco, Asco, Basidio, Deuteromycetes)", probability: "95%", primaryType: "Matching (90%)" },
          { name: "Monera: Archaebacteria vs Eubacteria", probability: "90%", primaryType: "Statement-Based (85%)" },
          { name: "Viruses, Viroids, Prions & Lichens", probability: "92%", primaryType: "Direct NCERT (85%)" }
        ],
        predictionNote: "Distinct spore types (Ascospores, Basidiospores) and sexual cycles of Fungi."
      },
      {
        id: "neet-bio-11-03",
        chapter: "Plant Kingdom",
        classLevel: "11th",
        avgQuestions: 3.5,
        avgMarks: 14,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "96%",
        recentTrend: [3, 3, 4, 4, 4],
        isLocked: true,
        subTopics: [
          { name: "Algae Classes (Chlorophyceae, Phaeophyceae, Rhodophyceae)", probability: "96%", primaryType: "Matching (90%)" },
          { name: "Gymnosperms: Life Cycle, Coralloid Roots & Mycorrhiza", probability: "90%", primaryType: "Statement-Based (80%)" },
          { name: "Pteridophytes: Heterospory & Prothallus", probability: "88%", primaryType: "Direct NCERT (80%)" }
        ],
        predictionNote: "Stored food and photosynthetic pigments table in Algae."
      },
      {
        id: "neet-bio-11-04",
        chapter: "Animal Kingdom",
        classLevel: "11th",
        avgQuestions: 4.0,
        avgMarks: 16,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "98%",
        recentTrend: [4, 4, 4, 5, 4],
        isLocked: true,
        subTopics: [
          { name: "Non-Chordates Phyla Characteristics & Examples", probability: "95%", primaryType: "Matching (90%)" },
          { name: "Chordates: Chondrichthyes vs Osteichthyes", probability: "92%", primaryType: "Statement-Based (85%)" },
          { name: "Mammals & Aves Unique Adaptations (Pneumatic Bones)", probability: "88%", primaryType: "Direct NCERT (80%)" }
        ],
        predictionNote: "Matching scientific names of organisms (e.g., Pila, Asterias, Fasciola) with excretory organs."
      },
      {
        id: "neet-bio-11-05",
        chapter: "Morphology of Flowering Plants",
        classLevel: "11th",
        avgQuestions: 3.5,
        avgMarks: 14,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "95%",
        recentTrend: [3, 4, 3, 4, 4],
        isLocked: true,
        subTopics: [
          { name: "Placentation Types (Marginal, Axile, Parietal, Free-central)", probability: "96%", primaryType: "Matching (95%)" },
          { name: "Aestivation (Valvate, Twisted, Imbricate, Vexillary)", probability: "92%", primaryType: "Diagram/Matching (90%)" },
          { name: "Floral Formula of Solanaceae, Fabaceae & Liliaceae", probability: "90%", primaryType: "Symbolic MCQ (85%)" }
        ],
        predictionNote: "Identifying correct floral diagram and formula of Solanaceae family."
      },
      {
        id: "neet-bio-11-06",
        chapter: "Anatomy of Flowering Plants",
        classLevel: "11th",
        avgQuestions: 3.0,
        avgMarks: 12,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "94%",
        recentTrend: [3, 3, 3, 4, 3],
        isLocked: true,
        subTopics: [
          { name: "Dicot vs Monocot Stem and Root Vascular Bundles", probability: "94%", primaryType: "Statement-Based (85%)" },
          { name: "Secondary Growth: Heartwood vs Sapwood, Spring vs Autumn Wood", probability: "90%", primaryType: "Assertion-Reason (80%)" }
        ],
        predictionNote: "Open, conjoint, endarch vascular bundle characteristic recognition."
      },
      {
        id: "neet-bio-11-07",
        chapter: "Structural Organisation in Animals (Animal Tissues & Cockroach)",
        classLevel: "11th",
        avgQuestions: 3.0,
        avgMarks: 12,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "93%",
        recentTrend: [2, 3, 3, 3, 4],
        isLocked: true,
        subTopics: [
          { name: "Epithelial Tissues Location (Squamous, Cuboidal, Ciliated)", probability: "95%", primaryType: "Matching (90%)" },
          { name: "Cockroach Anatomy (Alimentary Canal, Malpighian Tubules)", probability: "90%", primaryType: "Diagram/Direct (85%)" }
        ],
        predictionNote: "Epithelium lining the fallopian tubes, PCT and bronchioles."
      },
      {
        id: "neet-bio-11-08",
        chapter: "Cell: The Unit of Life",
        classLevel: "11th",
        avgQuestions: 4.5,
        avgMarks: 18,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "98%",
        recentTrend: [4, 5, 4, 5, 5],
        isLocked: false,
        subTopics: [
          { name: "Endomembrane System (ER, Golgi, Lysosome, Vacuole)", probability: "95%", primaryType: "Matching (85%)" },
          { name: "Mitochondria & Chloroplast Semi-autonomous Nature", probability: "90%", primaryType: "Statement-Based (80%)" },
          { name: "Cilia, Flagella (9+2 Arrangement) & Centrosome", probability: "88%", primaryType: "Direct NCERT (80%)" }
        ],
        predictionNote: "Cis and Trans face functions of Golgi Apparatus in glycoproteins synthesis."
      },
      {
        id: "neet-bio-11-09",
        chapter: "Biomolecules (Bio)",
        classLevel: "11th",
        avgQuestions: 3.0,
        avgMarks: 12,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "96%",
        recentTrend: [3, 3, 3, 4, 3],
        isLocked: true,
        subTopics: [
          { name: "Enzyme Kinetics, Competitive Inhibition ($K_m$ & $V_{max}$)", probability: "96%", primaryType: "Graphical / Statement (90%)" },
          { name: "Primary, Secondary, Tertiary Protein Structures & Bonds", probability: "90%", primaryType: "Direct NCERT (85%)" },
          { name: "Secondary Metabolites Table (Alkaloids, Toxins, Lectins)", probability: "94%", primaryType: "Matching (95%)" }
        ],
        predictionNote: "Direct matching question from NCERT Table of Secondary Metabolites (Concanavalin A, Ricin)."
      },
      {
        id: "neet-bio-11-10",
        chapter: "Cell Cycle and Cell Division",
        classLevel: "11th",
        avgQuestions: 3.5,
        avgMarks: 14,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "98%",
        recentTrend: [3, 4, 4, 3, 4],
        isLocked: false,
        subTopics: [
          { name: "Prophase I Sub-stages (Leptotene, Zygotene, Pachytene, Diplotene)", probability: "98%", primaryType: "Matching (95%)" },
          { name: "S-Phase DNA Duplication vs Chromosome Number", probability: "92%", primaryType: "Numerical Formula (85%)" },
          { name: "Metaphase Alignment & Anaphase Centromere Splitting", probability: "90%", primaryType: "Diagram/Direct (80%)" }
        ],
        predictionNote: "Stage of crossing over with recombinase enzyme (Pachytene)."
      },
      {
        id: "neet-bio-11-11",
        chapter: "Photosynthesis in Higher Plants",
        classLevel: "11th",
        avgQuestions: 3.5,
        avgMarks: 14,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "95%",
        recentTrend: [3, 4, 3, 4, 4],
        isLocked: true,
        subTopics: [
          { name: "$C_4$ Pathway (Kranz Anatomy, PEP Case, RuBisCO)", probability: "95%", primaryType: "Statement-Based (85%)" },
          { name: "Light Reaction: Non-cyclic & Cyclic Photophosphorylation", probability: "92%", primaryType: "Diagram/NCERT (85%)" },
          { name: "Chemiosmotic Hypothesis ($H^+$ Proton Gradient)", probability: "88%", primaryType: "Assertion-Reason (80%)" }
        ],
        predictionNote: "Number of ATP and NADPH consumed per glucose molecule in $C_3$ vs $C_4$ cycle."
      },
      {
        id: "neet-bio-11-12",
        chapter: "Respiration in Plants",
        classLevel: "11th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "93%",
        recentTrend: [2, 3, 2, 3, 3],
        isLocked: true,
        subTopics: [
          { name: "Electron Transport System (ETS Complex I to V)", probability: "94%", primaryType: "Matching (90%)" },
          { name: "Krebs (TCA) Cycle Steps & Decarboxylation Sites", probability: "90%", primaryType: "Statement-Based (85%)" },
          { name: "Respiratory Quotient (RQ) Values of Fats, Proteins, Carbs", probability: "88%", primaryType: "Direct NCERT (85%)" }
        ],
        predictionNote: "RQ value of Tripalmitin (0.7) and proteins (0.9)."
      },
      {
        id: "neet-bio-11-13",
        chapter: "Plant Growth and Development",
        classLevel: "11th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "94%",
        recentTrend: [2, 3, 2, 3, 3],
        isLocked: true,
        subTopics: [
          { name: "Plant Growth Regulators Functions (Auxin, Cytokinin, ABA, Ethylene)", probability: "96%", primaryType: "Matching (95%)" },
          { name: "Plasticity Examples (Cotton, Coriander, Larkspur)", probability: "90%", primaryType: "Direct NCERT (85%)" }
        ],
        predictionNote: "Matching hormone function: Fruit ripening (Ethylene), Bolting (Gibberellin), Stomata closure (ABA)."
      },
      {
        id: "neet-bio-11-14",
        chapter: "Breathing and Exchange of Gases",
        classLevel: "11th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "95%",
        recentTrend: [2, 2, 3, 3, 2],
        isLocked: true,
        subTopics: [
          { name: "Respiratory Volumes (Tidal, IRV, ERV, RV, VC)", probability: "96%", primaryType: "Matching (95%)" },
          { name: "Oxygen-Hemoglobin Dissociation Curve Shifts", probability: "92%", primaryType: "Graphical / Statement (90%)" }
        ],
        predictionNote: "Matching values of respiratory capacities: Functional Residual Capacity (FRC) and Vital Capacity (VC)."
      },
      {
        id: "neet-bio-11-15",
        chapter: "Body Fluids and Circulation",
        classLevel: "11th",
        avgQuestions: 3.0,
        avgMarks: 12,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "95%",
        recentTrend: [3, 3, 3, 3, 3],
        isLocked: true,
        subTopics: [
          { name: "ECG Waves (P-Wave, QRS Complex, T-Wave Significance)", probability: "96%", primaryType: "Matching / Diagram (95%)" },
          { name: "Cardiac Cycle Phases, Stroke Volume & Cardiac Output", probability: "90%", primaryType: "Numerical / Statement (85%)" },
          { name: "ABO Blood Grouping & Erythroblastosis Fetalis", probability: "88%", primaryType: "Direct NCERT (80%)" }
        ],
        predictionNote: "QRS complex representing depolarization of ventricles in a standard ECG."
      },
      {
        id: "neet-bio-11-16",
        chapter: "Excretory Products and Their Elimination",
        classLevel: "11th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "94%",
        recentTrend: [2, 3, 2, 3, 3],
        isLocked: true,
        subTopics: [
          { name: "Counter Current Mechanism in Henle's Loop & Vasa Recta", probability: "94%", primaryType: "Statement-Based (90%)" },
          { name: "Hormonal Regulation: RAAS System & ANF Mechanism", probability: "92%", primaryType: "Assertion-Reason (85%)" },
          { name: "Functions of Tubules (PCT, DCT, Collecting Duct)", probability: "86%", primaryType: "Matching (80%)" }
        ],
        predictionNote: "Role of ADH in water reabsorption and ANF causing vasodilation."
      },
      {
        id: "neet-bio-11-17",
        chapter: "Locomotion and Movement",
        classLevel: "11th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "94%",
        recentTrend: [2, 2, 3, 3, 2],
        isLocked: true,
        subTopics: [
          { name: "Types of Joints (Ball & Socket, Hinge, Pivot, Gliding)", probability: "96%", primaryType: "Matching (95%)" },
          { name: "Sliding Filament Theory of Muscle Contraction (Actin/Myosin)", probability: "90%", primaryType: "Statement-Based (85%)" },
          { name: "Skeletal Disorders (Myasthenia gravis, Tetany, Gout)", probability: "92%", primaryType: "Matching (90%)" }
        ],
        predictionNote: "Matching skeletal disorders with their etiology (e.g., Gout -> accumulation of uric acid crystals)."
      },
      {
        id: "neet-bio-11-18",
        chapter: "Neural Control and Coordination",
        classLevel: "11th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "93%",
        recentTrend: [2, 3, 2, 3, 2],
        isLocked: true,
        subTopics: [
          { name: "Generation & Conduction of Nerve Impulse ($Na^+ / K^+$ Pump)", probability: "92%", primaryType: "Statement-Based (85%)" },
          { name: "Structure of Human Eye & Ear Mechanisms", probability: "88%", primaryType: "Diagram/Matching (80%)" }
        ],
        predictionNote: "Resting membrane potential and influx of $Na^+$ during depolarization."
      },
      {
        id: "neet-bio-11-19",
        chapter: "Chemical Coordination and Integration",
        classLevel: "11th",
        avgQuestions: 3.5,
        avgMarks: 14,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "97%",
        recentTrend: [3, 4, 3, 4, 4],
        isLocked: true,
        subTopics: [
          { name: "Endocrine Glands & Hormone Matching (Thyroid, Adrenal, Pancreas)", probability: "98%", primaryType: "Matching (95%)" },
          { name: "Endocrine Disorders (Graves', Addison's, Cushing's, Acromegaly)", probability: "95%", primaryType: "Matching (90%)" },
          { name: "Mechanism of Peptide vs Steroid Hormone Action", probability: "88%", primaryType: "Assertion-Reason (80%)" }
        ],
        predictionNote: "Secondary messengers like cAMP for peptide hormones vs intracellular nuclear receptors for steroids."
      },
      // Class 12th Biology
      {
        id: "neet-bio-12-01",
        chapter: "Sexual Reproduction in Flowering Plants",
        classLevel: "12th",
        avgQuestions: 4.5,
        avgMarks: 18,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "98%",
        recentTrend: [4, 5, 4, 5, 5],
        isLocked: false,
        subTopics: [
          { name: "Microsporogenesis & Megasporogenesis Ploidy", probability: "95%", primaryType: "Numerical/Ploidy (85%)" },
          { name: "Outbreeding Devices to Prevent Inbreeding Depression", probability: "92%", primaryType: "Statement-Based (85%)" },
          { name: "Double Fertilization & Endosperm Types (PEN formation)", probability: "90%", primaryType: "Direct NCERT (80%)" }
        ],
        predictionNote: "Ploidy level calculations of embryo, aleurone layer and synergids."
      },
      {
        id: "neet-bio-12-02",
        chapter: "Human Reproduction",
        classLevel: "12th",
        avgQuestions: 4.0,
        avgMarks: 16,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "97%",
        recentTrend: [4, 4, 4, 4, 5],
        isLocked: true,
        subTopics: [
          { name: "Menstrual Cycle Hormonal Peak Sequence (LH Surge, FSH, Estrogen)", probability: "98%", primaryType: "Graphical / Statement (95%)" },
          { name: "Spermatogenesis vs Oogenesis Differences", probability: "92%", primaryType: "Matching / Diagram (85%)" },
          { name: "Fertilization, Cleavage & Blastocyst Implantation", probability: "88%", primaryType: "Direct NCERT (80%)" }
        ],
        predictionNote: "LH surge at day 14 causing ovulation of secondary oocyte."
      },
      {
        id: "neet-bio-12-03",
        chapter: "Reproductive Health",
        classLevel: "12th",
        avgQuestions: 3.0,
        avgMarks: 12,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "96%",
        recentTrend: [3, 3, 3, 3, 4],
        isLocked: true,
        subTopics: [
          { name: "Contraceptive Methods & IUD Classification (CuT, LNG-20, Progestasert)", probability: "98%", primaryType: "Matching (95%)" },
          { name: "Assisted Reproductive Technologies (ZIFT, GIFT, ICSI, IVF)", probability: "94%", primaryType: "Matching (90%)" },
          { name: "Sexually Transmitted Infections (STIs)", probability: "88%", primaryType: "Statement-Based (80%)" }
        ],
        predictionNote: "Hormone releasing IUDs (LNG-20) vs Copper releasing IUDs (Multiload 375, CuT)."
      },
      {
        id: "neet-bio-12-04",
        chapter: "Principles of Inheritance and Variation (Genetics 1)",
        classLevel: "12th",
        avgQuestions: 6.5,
        avgMarks: 26,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "99%",
        recentTrend: [6, 7, 6, 7, 7],
        isLocked: false,
        subTopics: [
          { name: "Mendelian Dihybrid Cross, Incomplete Dominance & Codominance", probability: "96%", primaryType: "Genetics Numerical (90%)" },
          { name: "Sex-Linked Inheritance (Hemophilia, Colorblindness Pedigrees)", probability: "94%", primaryType: "Pedigree Chart MCQ (90%)" },
          { name: "Chromosomal Disorders (Down, Klinefelter, Turner Syndromes)", probability: "95%", primaryType: "Matching (90%)" }
        ],
        predictionNote: "Karyotype of Turner syndrome (45, XO) vs Klinefelter syndrome (47, XXY)."
      },
      {
        id: "neet-bio-12-05",
        chapter: "Molecular Basis of Inheritance (Genetics 2)",
        classLevel: "12th",
        avgQuestions: 8.5,
        avgMarks: 34,
        yieldCategory: "High Yield / Moderate Effort",
        repeatProbability: "100%",
        recentTrend: [8, 8, 9, 9, 10],
        isLocked: false,
        subTopics: [
          { name: "Transcription in Prokaryotes vs Eukaryotes (RNA Polymerase I, II, III)", probability: "98%", primaryType: "Direct NCERT (90%)" },
          { name: "Genetic Code Properties (Degenerate, Universal, Non-overlapping)", probability: "96%", primaryType: "Matching (90%)" },
          { name: "Lac Operon System (Inducer, Repressor, Operator Gene)", probability: "95%", primaryType: "Statement-Based (90%)" },
          { name: "DNA Replication Enzymes (Helicase, Topoisomerase, Ligase)", probability: "92%", primaryType: "Matching (85%)" }
        ],
        predictionNote: "Functions of structural genes 'z', 'y', 'a' in Lac Operon (Beta-galactosidase, Permease, Transacetylase)."
      },
      {
        id: "neet-bio-12-06",
        chapter: "Evolution",
        classLevel: "12th",
        avgQuestions: 3.5,
        avgMarks: 14,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "95%",
        recentTrend: [3, 4, 3, 4, 4],
        isLocked: true,
        subTopics: [
          { name: "Homologous vs Analogous Organs (Divergent vs Convergent)", probability: "96%", primaryType: "Matching (95%)" },
          { name: "Hardy-Weinberg Equilibrium Equations ($p^2 + 2pq + q^2 = 1$)", probability: "94%", primaryType: "Numerical (90%)" },
          { name: "Human Evolution Order (Australopithecus, H. habilis, H. erectus, Neanderthal)", probability: "92%", primaryType: "Ordering MCQ (85%)" }
        ],
        predictionNote: "Cranial capacity matching: Neanderthal man (1400 cc), Homo erectus (900 cc)."
      },
      {
        id: "neet-bio-12-07",
        chapter: "Human Health and Disease",
        classLevel: "12th",
        avgQuestions: 4.0,
        avgMarks: 16,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "97%",
        recentTrend: [4, 4, 4, 4, 5],
        isLocked: true,
        subTopics: [
          { name: "Life Cycle of Plasmodium (Malaria Parasite in Mosquito & Man)", probability: "95%", primaryType: "Diagram/Statement (90%)" },
          { name: "Immunity: Active vs Passive, Antibodies ($IgA, IgG, IgE$)", probability: "94%", primaryType: "Matching (90%)" },
          { name: "Cancer & AIDS (HIV Replication, ELISA, Retrovirus)", probability: "90%", primaryType: "Statement-Based (85%)" },
          { name: "Drugs and Alcohol Abuse (Opioids, Cannabinoids, Coca Alkaloid)", probability: "92%", primaryType: "Matching (90%)" }
        ],
        predictionNote: "Matching drugs with receptors: Cannabinoids (Brain) vs Opioids (CNS & Gastrointestinal tract)."
      },
      {
        id: "neet-bio-12-08",
        chapter: "Microbes in Human Welfare",
        classLevel: "12th",
        avgQuestions: 2.5,
        avgMarks: 10,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "96%",
        recentTrend: [2, 3, 2, 3, 3],
        isLocked: true,
        subTopics: [
          { name: "Microbes in Industrial Products (Cyclosporin A, Statins, Streptokinase)", probability: "98%", primaryType: "Matching (95%)" },
          { name: "Sewage Treatment Plant (BOD, Aerobic vs Anaerobic Sludge)", probability: "92%", primaryType: "Statement-Based (85%)" },
          { name: "Biocontrol Agents & Biofertilizers (Bacillus thuringiensis, Trichoderma)", probability: "90%", primaryType: "Matching (85%)" }
        ],
        predictionNote: "Matching: Monascus purpureus (Statins) and Trichoderma polysporum (Cyclosporin A)."
      },
      {
        id: "neet-bio-12-09",
        chapter: "Biotechnology: Principles and Processes",
        classLevel: "12th",
        avgQuestions: 4.5,
        avgMarks: 18,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "98%",
        recentTrend: [4, 5, 4, 5, 5],
        isLocked: false,
        subTopics: [
          { name: "Restriction Endonucleases (Palindromic Sequences, Sticky Ends)", probability: "96%", primaryType: "Direct NCERT (90%)" },
          { name: "pBR322 Cloning Vector Features (ori, rop, ampR, tetR)", probability: "95%", primaryType: "Diagram/Matching (90%)" },
          { name: "Polymerase Chain Reaction (PCR: Denaturation, Annealing, Extension)", probability: "94%", primaryType: "Ordering MCQ (85%)" }
        ],
        predictionNote: "Thermostable Taq polymerase derived from Thermus aquaticus in PCR extension."
      },
      {
        id: "neet-bio-12-10",
        chapter: "Biotechnology and Its Applications",
        classLevel: "12th",
        avgQuestions: 3.5,
        avgMarks: 14,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "97%",
        recentTrend: [3, 4, 3, 4, 4],
        isLocked: true,
        subTopics: [
          { name: "Bt Cotton & Cry Genes (cryIAc, cryIIAb vs cryIAb)", probability: "96%", primaryType: "Matching (90%)" },
          { name: "RNA Interference (RNAi) in Tobacco Against Meloidogyne incognita", probability: "92%", primaryType: "Statement-Based (85%)" },
          { name: "Genetically Engineered Insulin (Humulin C-Peptide Removal)", probability: "94%", primaryType: "Direct NCERT (90%)" },
          { name: "Gene Therapy for ADA Deficiency", probability: "90%", primaryType: "Direct NCERT (85%)" }
        ],
        predictionNote: "C-peptide present in pro-insulin which is cleaved during maturation to functional insulin."
      },
      {
        id: "neet-bio-12-11",
        chapter: "Organisms and Populations (Ecology 1)",
        classLevel: "12th",
        avgQuestions: 3.5,
        avgMarks: 14,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "96%",
        recentTrend: [3, 4, 3, 4, 4],
        isLocked: true,
        subTopics: [
          { name: "Population Interactions (Mutualism, Commensalism, Amensalism, Parasitism)", probability: "98%", primaryType: "Matching (95%)" },
          { name: "Population Growth Curves (Exponential vs Logistic Verhulst-Pearl)", probability: "92%", primaryType: "Equation / Graphical (85%)" },
          { name: "Adaptations in Xerophytes & High Altitude Sickness", probability: "88%", primaryType: "Statement-Based (80%)" }
        ],
        predictionNote: "Examples: Sea anemone & Clown fish (Commensalism), Penicillium & Bacteria (Amensalism)."
      },
      {
        id: "neet-bio-12-12",
        chapter: "Ecosystem (Ecology 2)",
        classLevel: "12th",
        avgQuestions: 3.0,
        avgMarks: 12,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "95%",
        recentTrend: [3, 3, 3, 3, 4],
        isLocked: true,
        subTopics: [
          { name: "Ecological Pyramids (Inverted Pyramid of Biomass in Sea)", probability: "96%", primaryType: "Statement-Based (90%)" },
          { name: "Decomposition Steps (Fragmentation, Leaching, Catabolism, Humification)", probability: "92%", primaryType: "Ordering MCQ (85%)" },
          { name: "Productivity (GPP, NPP = GPP - R)", probability: "88%", primaryType: "Numerical Formula (80%)" }
        ],
        predictionNote: "Inverted pyramid of biomass in aquatic ecosystem and upright pyramid of energy."
      },
      {
        id: "neet-bio-12-13",
        chapter: "Biodiversity and Its Conservation (Ecology 3)",
        classLevel: "12th",
        avgQuestions: 3.5,
        avgMarks: 14,
        yieldCategory: "High Yield / Low Effort",
        repeatProbability: "97%",
        recentTrend: [3, 4, 3, 4, 4],
        isLocked: true,
        subTopics: [
          { name: "In-situ vs Ex-situ Conservation Strategies", probability: "98%", primaryType: "Matching (95%)" },
          { name: "Causes of Biodiversity Loss (The Evil Quartet)", probability: "94%", primaryType: "Direct NCERT (90%)" },
          { name: "Species-Area Relationship ($S = CA^z$) by Alexander von Humboldt", probability: "90%", primaryType: "Equation / Graphical (85%)" }
        ],
        predictionNote: "Classification: Botanical Gardens, Zoological Parks, Cryopreservation as Ex-situ vs National Parks as In-situ."
      }
    ],
    // NEET Physics & Chemistry share exact high yield chapters with JEE with NEET question distributions
    physics: [], // Uses the complete JEE physics dataset above with NEET exam weightage
    chemistry: [] // Uses the complete JEE chemistry dataset above with NEET exam weightage
  }
};
